import { storage } from './storage';
import { Prompt, Folder } from '../types';
import { STORAGE_KEYS } from '../constants/storageKeys';

interface StorageVersion {
  version: number;
}

const CURRENT_VERSION = 3;

// Ordered migration steps. Each is applied once, and the schema version is
// persisted (checkpointed) immediately after each step succeeds — so a later
// failure doesn't roll back and re-run already-applied steps on next launch.
const MIGRATIONS: Array<{ version: number; run: () => Promise<void> }> = [
  { version: 1, run: addUsageCountToPrompts },
  { version: 2, run: addIconToFolders },
  { version: 3, run: dedupeLibraryDuplicates },
];

export const migrateStorage = async () => {
  const version = (await storage.get(STORAGE_KEYS.VERSION)) as StorageVersion | undefined;
  // Use a nullish check so a genuine stored 0 isn't conflated with "missing".
  const currentVersion = version?.version ?? 0;

  if (currentVersion >= CURRENT_VERSION) {
    return;
  }

  for (const migration of MIGRATIONS) {
    if (currentVersion < migration.version) {
      await migration.run();
      // Checkpoint after each successful step.
      await storage.set(STORAGE_KEYS.VERSION, { version: migration.version });
    }
  }
};

// Declared as function declarations (hoisted) so the MIGRATIONS array above can
// reference them at module-load time without hitting the const temporal dead zone.
async function addUsageCountToPrompts(): Promise<void> {
  const prompts = (await storage.get(STORAGE_KEYS.PROMPTS)) as Prompt[] | undefined;
  if (prompts) {
    const updatedPrompts = prompts.map((prompt: Prompt) => ({
      ...prompt,
      usageCount: prompt.usageCount || 0,
    }));
    await storage.set(STORAGE_KEYS.PROMPTS, updatedPrompts);
  }
}

async function addIconToFolders(): Promise<void> {
  const folders = (await storage.get(STORAGE_KEYS.FOLDERS)) as Folder[] | undefined;
  if (folders) {
    const updatedFolders = folders.map((folder: Folder) => ({
      ...folder,
      icon: folder.icon || 'Folder',
    }));
    await storage.set(STORAGE_KEYS.FOLDERS, updatedFolders);
  }
}

/**
 * One-time cleanup for libraries duplicated by the pre-1.0.5 default-prompts
 * import, which minted fresh uuids on every run: content-identical prompts
 * (and same-named folders) accumulated under different ids, which the
 * id-keyed sync then faithfully preserved in Firestore.
 *
 * Folders sharing a (case-insensitive) name are merged into one survivor and
 * prompts are re-pointed at it; prompts identical in title/text/description/
 * tags/folder are collapsed to one, folding usage counts together. Removed
 * items disappear from local storage only — the sync engine detects the
 * missing ids on its next cycle and pushes tombstones, so the duplicates are
 * cleaned out of Firestore (and other devices) too instead of resurrecting.
 *
 * Survivor selection is deterministic (oldest `lastChanged`, then lowest id;
 * folders: lowest `order`, then lowest id) so two devices running this
 * migration independently keep the SAME copy — if each kept a different one,
 * their tombstones would delete both.
 */
async function dedupeLibraryDuplicates(): Promise<void> {
  const storedPrompts = (await storage.get(STORAGE_KEYS.PROMPTS)) as Prompt[] | undefined;
  const storedFolders = (await storage.get(STORAGE_KEYS.FOLDERS)) as Folder[] | undefined;
  const prompts = Array.isArray(storedPrompts) ? storedPrompts : [];
  const folders = Array.isArray(storedFolders) ? storedFolders : [];
  if (prompts.length === 0 && folders.length === 0) {
    return;
  }

  // --- Folders: merge copies that share a name -----------------------------
  const folderWins = (a: Folder, b: Folder): Folder => {
    const orderA = a.order ?? Number.MAX_SAFE_INTEGER;
    const orderB = b.order ?? Number.MAX_SAFE_INTEGER;
    if (orderA !== orderB) return orderA < orderB ? a : b;
    return a.id <= b.id ? a : b;
  };
  const survivorByName = new Map<string, Folder>();
  for (const folder of folders) {
    const key = (folder.name ?? '').trim().toLowerCase();
    if (!key || folder.isSpecial) continue;
    const current = survivorByName.get(key);
    survivorByName.set(key, current ? folderWins(current, folder) : folder);
  }
  const folderRemap = new Map<string, string>();
  const emittedFolderNames = new Set<string>();
  const dedupedFolders = folders.filter(folder => {
    const key = (folder.name ?? '').trim().toLowerCase();
    if (!key || folder.isSpecial) return true;
    const survivor = survivorByName.get(key)!;
    if (survivor.id !== folder.id) {
      folderRemap.set(folder.id, survivor.id);
      return false;
    }
    // Guard against exact same-id copies of the survivor in the array.
    if (emittedFolderNames.has(key)) return false;
    emittedFolderNames.add(key);
    return true;
  });

  const remappedPrompts = prompts.map(prompt =>
    folderRemap.has(prompt.folder) ? { ...prompt, folder: folderRemap.get(prompt.folder)! } : prompt
  );

  // --- Prompts: collapse content-identical copies --------------------------
  const promptWins = (a: Prompt, b: Prompt): Prompt => {
    const changedA = a.lastChanged ?? 0;
    const changedB = b.lastChanged ?? 0;
    if (changedA !== changedB) return changedA < changedB ? a : b;
    return a.id <= b.id ? a : b;
  };
  const contentKey = (p: Prompt): string =>
    [
      p.title ?? '',
      p.text ?? '',
      p.description ?? '',
      (p.tags ?? []).join(','),
      p.folder ?? '',
    ].join('\u0000');
  const survivorByContent = new Map<string, Prompt>();
  const usageByContent = new Map<string, number>();
  for (const prompt of remappedPrompts) {
    const key = contentKey(prompt);
    const current = survivorByContent.get(key);
    survivorByContent.set(key, current ? promptWins(current, prompt) : prompt);
    usageByContent.set(key, Math.max(usageByContent.get(key) ?? 0, prompt.usageCount ?? 0));
  }
  const emittedPromptKeys = new Set<string>();
  const dedupedPrompts: Prompt[] = [];
  for (const prompt of remappedPrompts) {
    const key = contentKey(prompt);
    const survivor = survivorByContent.get(key)!;
    // Emit only the survivor, once (an exact same-id copy would also match).
    if (survivor.id !== prompt.id || emittedPromptKeys.has(key)) continue;
    emittedPromptKeys.add(key);
    dedupedPrompts.push({
      ...survivor,
      usageCount: usageByContent.get(key) ?? survivor.usageCount ?? 0,
    });
  }

  const foldersChanged = dedupedFolders.length !== folders.length;
  const promptsChanged = dedupedPrompts.length !== prompts.length || folderRemap.size > 0;
  if (foldersChanged) {
    await storage.set(STORAGE_KEYS.FOLDERS, dedupedFolders);
  }
  if (promptsChanged) {
    await storage.set(STORAGE_KEYS.PROMPTS, dedupedPrompts);
  }
}
